import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

import { HomeService } from '../../../../services/home.service';
import { ExcelExportService } from '../../../../services/excel-export.service';
import { UserService } from '../../../../services/user';
import { ItemsService } from '../../../../services/items.service';
import { WarehouseTypeService } from '../../../../services/warehouse-type';

@Component({
  selector: 'app-allusersmobile',
  templateUrl: './allusersmobile.component.html',
  styleUrl: './allusersmobile.component.scss',
})
export class AllusersmobileComponent implements OnInit, OnDestroy {
  allData: any[] = [];
  allUsersRaw: any[] = [];

  selectedUserType: string = '';
  filteredUserTypes: { value: string; label: string }[] = [];

  userTypes = [
    { value: '', label: 'Users' },
    { value: '1', label: 'admin' },
    { value: '2', label: 'Affiliation' },
    { value: '3', label: 'Page A' },
    { value: '4', label: 'B' },
    { value: '5', label: 'C' },
    { value: '6', label: 'subadmin' },
    { value: '7', label: 'employee' },
    { value: '8', label: 'deliveryhero' },
    { value: '9', label: 'client' },
    { value: '10', label: 'super admin' },
  ];

  contentReady: boolean = true;
  noData: boolean = false;
  showSearch: boolean = false;
  searchValue: string = '';
  searchTimer: any;

  warehouseId?: any;
  allWareHouses: any[] = [];

  isAr: boolean = false;
  isBrowser: boolean = false;
  canManageUsers: boolean = false;

  // Infinite Scroll Pagination
  page: number = 1;
  pageSize: number = 10;
  totalUsers: number = 0;
  totalPages: number = 1;
  from: number = 0;
  to: number = 0;
  isLoadingMore: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private _homeService: HomeService,
    public warehouseTypeService: WarehouseTypeService,
    private _userService: UserService,
    private _ItemsService: ItemsService,
    private excelExportService: ExcelExportService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.isAr = this.isBrowser
      ? localStorage.getItem('language') === 'ar'
      : false;

    this.warehouseId = this.isBrowser
      ? localStorage.getItem('posId')
      : null;

    this.contentReady = true;

    this._userService.getAccountType().subscribe({
      next: (data) => {
        const userType = String(data?.['user_type'] ?? '');

        this.canManageUsers = userType === '1' || userType === '10';

        this.setFilteredUserTypes();

        this._ItemsService.getWearHouses().subscribe({
          next: (res) => {
            this.allWareHouses = res?.warehouse || [];

            if (!this.warehouseId && this.allWareHouses.length > 0) {
              this.warehouseId = this.allWareHouses[0].id;

              if (this.isBrowser) {
                localStorage.setItem('posId', String(this.warehouseId));
              }
            }

            this.loadUsers(1, true);
          },
          error: () => {
            this.loadUsers(1, true);
          },
        });
      },
      error: (error) => {
        console.error(error);
        this.contentReady = false;
        this.noData = true;
      },
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimer);
  }

  // ===============================
  // INFINITE SCROLL
  // ===============================

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (!this.isBrowser) return;
    if (this.contentReady || this.isLoadingMore) return;
    if (this.page >= this.totalPages) return;

    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;

    const bottomReached =
      scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.loadMore();
    }
  }

  loadMore(): void {
    if (this.isLoadingMore) return;
    if (this.page >= this.totalPages) return;

    const nextPage = this.page + 1;
    this.loadUsers(nextPage, false);
  }

  // ===============================
  // DATA
  // ===============================

  loadUsers(page: number = 1, reset: boolean = true): void {
    if (!this.warehouseId) {
      this.resetUsersList();
      return;
    }

    if (reset) {
      this.contentReady = true;
      this.isLoadingMore = false;
      this.page = 1;
      this.allData = [];
      this.allUsersRaw = [];
    } else {
      this.isLoadingMore = true;
    }

    const search = this.searchValue.trim();
    const hasSearch = search.length > 0;

    const request$ = hasSearch
      ? this._homeService.searchUsersByWarehouse(
          this.warehouseId,
          page,
          this.pageSize,
          {
            search,
            type: this.selectedUserType,
          }
        )
      : this._homeService.getUsersByWarehouse(
          this.warehouseId,
          page,
          this.pageSize,
          this.selectedUserType
        );

    request$.subscribe({
      next: (res) => {
        const paginator = res?.users;
        const usersList = paginator?.data || [];

        this.page = Number(paginator?.current_page || page);
        this.pageSize = Number(paginator?.per_page || this.pageSize);
        this.totalUsers = Number(paginator?.total || 0);
        this.totalPages = Number(paginator?.last_page || 1);
        this.from = Number(paginator?.from || 0);
        this.to = Number(paginator?.to || 0);

        this.allData = reset
          ? usersList
          : [...this.allData, ...usersList];

        this.allUsersRaw = [...this.allData];

        this.noData = this.allData.length === 0;
        this.contentReady = false;
        this.isLoadingMore = false;
      },
      error: (err) => {
        console.error(err);

        if (reset) {
          this.allData = [];
          this.allUsersRaw = [];
          this.noData = true;
          this.contentReady = false;
        }

        this.isLoadingMore = false;
      },
    });
  }

  resetUsersList(): void {
    this.allData = [];
    this.allUsersRaw = [];

    this.page = 1;
    this.totalUsers = 0;
    this.totalPages = 1;
    this.from = 0;
    this.to = 0;

    this.noData = true;
    this.contentReady = false;
    this.isLoadingMore = false;
  }

  // ===============================
  // FILTERS
  // ===============================

  setFilteredUserTypes(): void {
    this.filteredUserTypes = this.userTypes.filter((type) =>
      this.shouldShowUserType(type.value)
    );

    if (
      this.selectedUserType &&
      !this.filteredUserTypes.some(
        (type) => type.value === this.selectedUserType
      )
    ) {
      this.selectedUserType = '';
    }
  }

  shouldShowUserType(value: string): boolean {
    switch (value) {
      case '2':
      case '3':
      case '4':
      case '5':
      case '9':
        return this.warehouseTypeService.isCompany();

      case '8':
        return (
          this.warehouseTypeService.isCompany() ||
          this.warehouseTypeService.isShipping()
        );

      default:
        return true;
    }
  }

  applyWareHouse(id: any): void {
    this.warehouseId = id;

    if (this.isBrowser) {
      localStorage.setItem('posId', String(id));
    }

    this.searchValue = '';
    this.selectedUserType = '';
    this.showSearch = false;

    this.loadUsers(1, true);
  }

  filterByUserType(type: string): void {
    this.selectedUserType = type;
    this.loadUsers(1, true);
  }

  applyFilter(event: Event): void {
    this.searchValue = (event.target as HTMLInputElement).value.trim();

    clearTimeout(this.searchTimer);

    this.searchTimer = setTimeout(() => {
      this.loadUsers(1, true);
    }, 400);
  }

  clearSearch(input?: HTMLInputElement): void {
    this.searchValue = '';

    if (input) {
      input.value = '';
    }

    this.loadUsers(1, true);
  }

  toggleSearch(): void {
    this.showSearch = !this.showSearch;

    if (!this.showSearch && this.searchValue) {
      this.searchValue = '';
      this.loadUsers(1, true);
    }
  }

  // ===============================
  // HELPERS
  // ===============================

  getSelectedWarehouseName(): string {
    const selected = this.allWareHouses.find(
      (item) => String(item.id) === String(this.warehouseId)
    );

    return selected?.name || '';
  }

  getUserType(user: any): string {
    return String(user?.type ?? '');
  }

  getUserTypeLabel(user: any): string {
    const type = this.getUserType(user);

    switch (type) {
      case '10':
        return 'super admin';
      case '6':
        return 'subadmin';
      case '7':
        return 'employee';
      case '8':
        return 'deliveryhero';
      case '2':
        return 'Affiliation';
      case '3':
        return 'Page A';
      case '4':
        return 'B';
      case '5':
        return 'C';
      case '9':
        return 'client';
      case '1':
      default:
        return 'admin';
    }
  }

  getBadgeClass(user: any): string {
    const type = this.getUserType(user);

    switch (type) {
      case '10':
        return 'badge-super';
      case '6':
        return 'badge-subadmin';
      case '7':
        return 'badge-employee';
      case '8':
        return 'badge-hero';
      case '2':
        return 'badge-affiliation';
      case '3':
        return 'badge-page';
      case '4':
        return 'badge-b';
      case '5':
        return 'badge-c';
      case '9':
        return 'badge-client';
      default:
        return 'badge-admin';
    }
  }

  getAvatarText(user: any): string {
    const name =
      user?.first_name ||
      user?.username ||
      user?.phone ||
      '?';

    return String(name).charAt(0).toUpperCase();
  }

  hasMore(): boolean {
    return this.page < this.totalPages;
  }

  go(url: string): void {
    if (!url) return;

    const safeUrl =
      url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;

    window.open(safeUrl, '_blank');
  }

  edituser(itemobj: any): void {
    this.router.navigate(['/Resigter'], { queryParams: itemobj });
  }

  deleteuser(id: string): void {
    Swal.fire({
      title: this.isAr ? 'متأكد؟' : 'Are You Sure?',
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'نعم' : 'YES',
      cancelButtonText: this.isAr ? 'لا' : 'no',
      confirmButtonColor: '#dc2626',
    }).then((result) => {
      if (result.isConfirmed) {
        this.contentReady = true;

        this._homeService.deleteUser(id).subscribe({
          next: () => {
            this.loadUsers(1, true);
          },
          error: (err) => {
            console.error(err);
            this.contentReady = false;
          },
        });
      }
    });
  }

  exportToExcel(): void {
    this.excelExportService.exportToExcel(
      'userstable-mobile',
      'BlueBox Users',
      ['delete']
    );
  }

  showpassword(password: any): void {
    Swal.fire({
      title: this.isAr ? 'كلمة المرور' : 'User Password',
      text: `${password || '-'}`,
      icon: 'info',
      confirmButtonText: this.isAr ? 'تمام' : 'OK',
      confirmButtonColor: '#1E3A8A',
    });
  }
}